// Demo entries — add a new business by adding an object here.
// Each demo renders at /demos/:slug using the DemoTemplate.
//
// Required: slug, name, tagline, industry, location, cover, palettes (or palette),
//           fontDisplay, services, hours, contact.
// Optional: defaultMode, stats, about, featured, gallery, testimonials,
//           testimonial (legacy single), faq.

const demos = [
  {
    slug: 'bakery',
    name: 'Blue Door Bakery',
    tagline: 'A neighborhood bakery built on butter, time, and good company.',
    industry: 'Bakery',
    location: 'Anytown, USA',
    cover: '/images/demos/bakery/g1.webp',
    palettes: {
      light: { bg: '#FAF6EE', surface: '#FFFFFF', text: '#23170E', muted: '#7A6856', accent: '#9A3412', accent2: '#D97706' },
      dark:  { bg: '#1A130C', surface: '#251A12', text: '#F4ECDD', muted: '#A89683', accent: '#FB923C', accent2: '#F59E0B' },
    },
    defaultMode: 'light',
    fontDisplay: '"Fraunces", serif',
    stats: [
      { label: 'Baking since', value: '2014' },
      { label: 'Google reviews', value: '4.9 ★' },
      { label: 'Loaves a week', value: '1,200+' },
      { label: 'Saturdays a year', value: '52' },
    ],
    services: [
      { title: 'Daily Breads', desc: 'Sourdough, country loaves, baguettes, and rye — all naturally leavened, baked fresh before sunrise.' },
      { title: 'Pastries & Viennoiseries', desc: 'Laminated croissants, pain au chocolat, kouign-amann, scones, and a rotating seasonal pastry case.' },
      { title: 'Custom Cakes', desc: 'Birthdays, weddings, retirements, and just-because. Order 48 hours ahead — we build it from scratch.' },
      { title: 'Coffee & Sit-Down', desc: 'Locally roasted espresso, pour-overs, and matcha. Stay a while — we have books and good light.' },
    ],
    about: {
      heading: 'A bakery, the way they used to be.',
      body: `We started Blue Door in 2014 with one deck oven, a small starter named Hilde, and the stubborn idea that bread should taste like the place it came from.\n\nTen years later, Hilde is still doing most of the heavy lifting — every loaf we sell is built on a 24-hour cold ferment that gives the crumb its tang and chew. The pastries are laminated by hand each morning, the coffee comes from the roaster three blocks over, and the seat by the window is always free if you bring a book.`,
      signature: 'The Blue Door team',
    },
    featured: {
      kicker: 'Today\'s case',
      heading: 'What we\'re baking this week',
      subheading: 'The case rotates — call ahead if you have your eye on something specific.',
      navLabel: 'Menu',
      items: [
        { name: 'Country Sourdough', desc: 'Our flagship — wild yeast, long ferment, dark crust. Whole or half loaf.', price: '$8 / $4.50' },
        { name: 'Pain au Chocolat', desc: 'Two batons of dark Valrhona, wrapped in 27 layers of butter croissant dough.', price: '$4.25', tag: 'Daily' },
        { name: 'Almond Croissant', desc: 'Twice-baked, frangipane-filled, dusted in icing sugar. Best with a cortado.', price: '$4.75' },
        { name: 'Maple Pecan Sticky Bun', desc: 'Real maple, toasted pecans, soft enriched dough. Comes warm out of the oven at 8 AM.', price: '$5', tag: 'Weekends' },
        { name: 'Quiche of the Day', desc: 'Rotating — leek & gruyère Mon/Wed, mushroom-thyme Tue/Thu, bacon-cheddar Fri.', price: '$7.50' },
        { name: 'Olive Fougasse', desc: 'Castelvetrano olives, rosemary, flaky sea salt. Reheats beautifully.', price: '$9' },
      ],
    },
    gallery: [
      '/images/demos/bakery/g1.webp',
      '/images/demos/bakery/g2.webp',
      '/images/demos/bakery/g3.webp',
      '/images/demos/bakery/g4.webp',
    ],
    hours: [
      { day: 'Tue – Fri', time: '6:30 AM – 6:00 PM' },
      { day: 'Saturday', time: '7:00 AM – 4:00 PM' },
      { day: 'Sunday', time: '8:00 AM – 2:00 PM' },
      { day: 'Monday', time: 'Closed' },
    ],
    testimonials: [
      { quote: 'The kind of place every neighborhood wishes it had. The croissants are honestly better than anything I had in Paris last summer.', author: 'Anna M., neighbor & regular', rating: 5 },
      { quote: "Blue Door's sourdough is the only bread I'll buy anymore. The crumb is gorgeous and it stays good for a full week if you wrap it right.", author: 'Daniel R., home cook', rating: 5 },
      { quote: 'Got my daughter\'s wedding cake here — three tiers, hand-piped, exactly what she sketched on a napkin. They nailed every detail.', author: 'Patricia W., mother of the bride', rating: 5 },
    ],
    faq: [
      { q: 'How far ahead do I need to order a custom cake?', a: '48 hours for our standard menu, two weeks for tiered or wedding cakes. Drop in or call (555) 555-0142 to start a consultation — first one is on the house.' },
      { q: 'Do you offer gluten-free options?', a: 'Yes — we have GF brownies, almond cakes, and a rotating GF cookie daily. We are not a dedicated GF facility, so trace cross-contact is possible.' },
      { q: 'Can I order a bread CSA / weekly subscription?', a: 'Absolutely. Pick your loaves and pickup day, we put a bag aside for you each week. Sign up at the counter or email us.' },
      { q: 'Do you cater?', a: 'Pastry trays start at $45 and serve 8–10. We do morning meetings, baby showers, and brunches. Two days notice for trays, a week for anything bigger.' },
    ],
    contact: { phone: '(555) 555-0142', email: 'hello@bluedoorbakery.example', address: '142 Maple Street, Anytown' },
  },
  {
    slug: 'wild-stem',
    name: 'Wild Stem Floral',
    tagline: 'Foraged, farm-direct, designed by hand. Modern florals for weddings, events, and everyday.',
    industry: 'Florist',
    location: 'Portland, OR',
    cover: '/images/demos/wild-stem/cover.webp',
    palettes: {
      light: { bg: '#F8F4ED', surface: '#FFFFFF', text: '#1F2A1A', muted: '#7C8771', accent: '#A04E3F', accent2: '#C97064' },
      dark:  { bg: '#13180F', surface: '#1F2818', text: '#F5EFE3', muted: '#A6B095', accent: '#E07A5F', accent2: '#F2A8A0' },
    },
    defaultMode: 'light',
    fontDisplay: '"Cormorant Garamond", serif',
    stats: [
      { label: 'Designing since', value: '2018' },
      { label: 'Google reviews', value: '4.9 ★' },
      { label: 'Weddings a year', value: '80+' },
      { label: 'Local farm partners', value: '14' },
    ],
    services: [
      { title: 'Weddings & Events', desc: 'Full-service floral design — bouquets, ceremony installs, tablescapes, and day-of styling.' },
      { title: 'Subscriptions', desc: 'Weekly, bi-weekly, or monthly farm-fresh stems delivered to your home or office.' },
      { title: 'Sympathy & Tributes', desc: 'Thoughtful, modern arrangements for memorials and remembrances. Same-day in Portland.' },
      { title: 'Workshops', desc: 'Seasonal classes — wreaths, holiday arrangements, hand-tied bouquets. BYO wine.' },
    ],
    about: {
      heading: 'PNW-grown, hand-designed, never wholesale-flown.',
      body: `Wild Stem started in 2018 as a Saturday market stall with a single foraging bucket and a clear opinion: every flower we sell should come from a farm we can drive to.\n\nSeven years later we work with fourteen growers across Oregon and southern Washington — peony farms in Hillsboro, dahlia fields in Sandy, foraged greens from the Coast Range. Nothing flown in from South America, nothing dyed, nothing wrapped in plastic. Just stems that belong to this place, designed by hand for yours.`,
      signature: 'Mara K., founder',
    },
    featured: {
      kicker: 'Order online',
      heading: 'Bouquets, subscriptions & consults',
      subheading: 'Same-day delivery in Portland for orders placed by 11 AM. All bouquets are designer\'s choice from what\'s freshest that week.',
      navLabel: 'Order',
      items: [
        { name: 'Petite Bouquet', desc: 'Hand-tied seasonal stems, ~6 varieties. Perfect bedside or "thinking of you".', price: '$48' },
        { name: 'Designer Bouquet', desc: 'Our most-ordered. Lush, asymmetric, ~12 varieties of seasonal stems and foraged greens.', price: '$88', tag: 'Most popular' },
        { name: 'Statement Arrangement', desc: 'In a glass vessel, designer\'s choice. The "wow" gift — for a host, an apology, a milestone.', price: '$165' },
        { name: 'Monthly Subscription', desc: 'One designer bouquet a month, delivered. 10% off everyday orders for subscribers.', price: '$72 / mo' },
        { name: 'Wedding Consultation', desc: '90 minutes with a lead designer + sample bud-vase + written quote. Credited toward booking.', price: '$125' },
        { name: 'Workshop Seat', desc: 'Two-hour seasonal class — wreaths, hand-ties, holiday tabletops. Wine + take-home included.', price: '$95', tag: 'Monthly' },
      ],
    },
    gallery: [
      '/images/demos/wild-stem/g1.webp',
      '/images/demos/wild-stem/g2.webp',
      '/images/demos/wild-stem/g3.webp',
      '/images/demos/wild-stem/g4.webp',
    ],
    hours: [
      { day: 'Tue – Fri', time: '10:00 AM – 6:00 PM' },
      { day: 'Saturday', time: '10:00 AM – 4:00 PM' },
      { day: 'Sunday', time: 'By appointment' },
      { day: 'Monday', time: 'Closed' },
    ],
    testimonials: [
      { quote: 'Mara designed our wedding florals and it felt like she\'d known us for years. Wild, garden-y, totally us — every photographer\'s favorite shot of the day was the bouquet.', author: 'Amelia & Ross G., wedding clients', rating: 5 },
      { quote: 'I started a monthly subscription for my mom across town. She texts me a photo every time it arrives. Best $72 I spend each month.', author: 'Theo M.', rating: 5 },
      { quote: 'Same-day sympathy arrangement when my dad passed. They handled it with so much care — the design was simple, beautiful, and exactly right for him.', author: 'Sasha L.', rating: 5 },
    ],
    faq: [
      { q: 'How far ahead do I need to book a wedding?', a: 'We book most Portland-area weddings 6–12 months out, especially May–October. Smaller weekday events sometimes have 4–8 weeks notice. Always worth asking.' },
      { q: 'Where do you deliver?', a: 'Same-day delivery throughout Portland and inner suburbs (Beaverton, Lake Oswego, Milwaukie) for orders placed by 11 AM. Next-day everywhere else in the metro.' },
      { q: 'Can I request specific flowers?', a: 'For weddings and events, absolutely — that\'s part of the consult. For everyday bouquets we work designer\'s choice, since freshness varies week to week. Tell us what you love and what you don\'t.' },
      { q: 'Where do your flowers come from?', a: '90%+ from Oregon and Washington farms. We post the week\'s sourcing list on Instagram every Tuesday. Imported stems only when local season is fully out (rare — January, mostly).' },
    ],
    contact: { phone: '(503) 555-0184', email: 'hello@wildstemfloral.example', address: '1420 NW 23rd Ave, Portland, OR' },
  },
  {
    slug: 'apex-plumbing',
    name: 'Apex Plumbing & Drain',
    tagline: '24/7 emergency plumbing for homes and businesses in the Tri-Cities. Licensed, bonded, on time.',
    industry: 'Plumbing',
    location: 'Pasco, WA',
    cover: '/images/demos/apex-plumbing/cover.webp',
    palettes: {
      light: { bg: '#F0F9FF', surface: '#FFFFFF', text: '#0C2A3D', muted: '#475569', accent: '#0284C7', accent2: '#F59E0B' },
      dark:  { bg: '#0F172A', surface: '#1E293B', text: '#F8FAFC', muted: '#94A3B8', accent: '#0EA5E9', accent2: '#FACC15' },
    },
    defaultMode: 'dark',
    fontDisplay: '"Barlow Condensed", sans-serif',
    stats: [
      { label: 'Dispatch', value: '24/7' },
      { label: 'Jobs / year', value: '800+' },
      { label: 'Google reviews', value: '4.9 ★' },
      { label: 'On-time arrival', value: '97%' },
    ],
    services: [
      { title: 'Emergency Repair', desc: 'Burst pipe, sewage backup, no hot water. We answer 24/7 and dispatch within the hour.' },
      { title: 'Drain & Sewer', desc: 'Hydro jetting, camera inspection, root removal. Clear lines, written reports.' },
      { title: 'Water Heaters', desc: 'Tank, tankless, heat pump. Repair or full replacement, same-day in most cases.' },
      { title: 'Leak Detection', desc: 'Acoustic and thermal-imaging leak location. Find it once, fix it right — no exploratory drywall.' },
      { title: 'Remodels & New Construction', desc: 'Bath and kitchen rough-ins for contractors and homeowners. Permit-ready.' },
      { title: 'Commercial Service', desc: 'Restaurants, retail, multi-family. Standing-account pricing, scheduled maintenance, same-day priority.' },
    ],
    serviceAreas: ['Pasco', 'Kennewick', 'Richland', 'West Richland', 'Burbank', 'Finley'],
    serviceZipPrefix: '993',
    about: {
      heading: 'Owner-operator. Honest pricing. Real warranties.',
      body: `I started Apex in 2017 after twelve years working for the bigger outfits. Tired of seeing customers get up-charged on emergency calls, tired of the "we'll have to send a tech out for an estimate" runaround.\n\nApex runs flat. Tom dispatches, Tom shows up, Tom hands you a written quote before he turns a wrench. Two trucks, four licensed plumbers, no commission, no scripts. We tell you what's wrong, what it'll cost, and we stand behind the work for a year.`,
      signature: 'Tom Reyes, Master Plumber',
    },
    featured: {
      kicker: 'Common pricing',
      heading: 'What things actually cost',
      subheading: 'Up-front pricing on the calls we get most. Diagnosis is always free if we end up doing the work.',
      navLabel: 'Pricing',
      items: [
        { name: 'Diagnostic visit', desc: 'On-site assessment + written estimate. Free if you book the repair with us.', price: '$89' },
        { name: 'Drain clear (single fixture)', desc: 'Sink, tub, or toilet line. Includes camera if needed.', price: 'from $189' },
        { name: 'Main sewer hydro jet', desc: '50 ft hydro jet + camera inspection + before/after report.', price: 'from $495' },
        { name: 'Water heater swap (40 gal)', desc: 'Standard tank-to-tank replacement, 6-year warranty, haul-off included.', price: 'from $1,650' },
        { name: 'Tankless install', desc: 'Navien or Rinnai. Full venting, gas line check, 12-year heat exchanger.', price: 'from $3,800' },
        { name: 'After-hours emergency', desc: 'Nights, weekends, holidays. Flat dispatch + parts + labor — no surprise upcharges.', price: '$179 dispatch' },
      ],
    },
    gallery: [
      '/images/demos/apex-plumbing/g1.webp?v=2',
      '/images/demos/apex-plumbing/g2.webp?v=2',
      '/images/demos/apex-plumbing/g3.webp?v=2',
      '/images/demos/apex-plumbing/g4.webp?v=2',
    ],
    hours: [
      { day: 'Emergency Service', time: '24 / 7 / 365' },
      { day: 'Office Hours', time: 'Mon – Fri, 7 AM – 6 PM' },
    ],
    testimonials: [
      { quote: 'Pipe burst at 11 PM on a Sunday. Apex was at our door in 40 minutes and had us dry by 2 AM. Honest pricing, no panic upcharge.', author: 'Ron D., homeowner', rating: 5 },
      { quote: 'Tom replaced our 25-year-old water heater the same day I called. Quote was the price. No hidden fees, no upsells.', author: 'Kelly M., Richland', rating: 5 },
      { quote: 'They camera-scoped our sewer line, showed me the exact crack, gave three options at three price points. Felt like talking to my brother-in-law.', author: 'Marcus T., Kennewick', rating: 5 },
    ],
    faq: [
      { q: 'What\'s your service area?', a: 'Pasco, Kennewick, Richland, West Richland, and Burbank. We\'ll go further out for commercial accounts on standing contracts.' },
      { q: 'Are you licensed and insured?', a: 'Yes — WA license #APEXPP814R3, $2M general liability, $1M auto. Master plumber on-staff. Send us the certificate-of-insurance request and we\'ll have it to you in an hour.' },
      { q: 'Do you offer free estimates?', a: 'For repairs over $500, yes — on-site assessment is free. For diagnostic-only visits we charge $89, refunded if you book the work with us.' },
      { q: 'What\'s your warranty?', a: 'One year on labor for any repair, plus the manufacturer\'s warranty on parts. Water heaters carry full 6-year coverage including labor.' },
    ],
    contact: { phone: '(509) 555-0188', email: 'dispatch@apexplumbing.example', address: '1840 Court St, Pasco, WA' },
  },
  {
    slug: 'riverside-dental',
    name: 'Riverside Family Dental',
    tagline: 'Gentle, modern dentistry for the whole family. New patients welcome.',
    industry: 'Dental Practice',
    location: 'Boise, ID',
    cover: '/images/demos/riverside-dental/cover.webp',
    palettes: {
      light: { bg: '#F0F9FF', surface: '#FFFFFF', text: '#0C4A6E', muted: '#475569', accent: '#0891B2', accent2: '#34D399' },
      dark:  { bg: '#082F49', surface: '#0F4A6B', text: '#ECFEFF', muted: '#93C5FD', accent: '#22D3EE', accent2: '#34D399' },
    },
    defaultMode: 'light',
    fontDisplay: '"Manrope", sans-serif',
    stats: [
      { label: 'Patients served', value: '12,000+' },
      { label: 'Google reviews', value: '4.9 ★' },
      { label: 'Years in practice', value: '18' },
      { label: 'Insurance plans', value: 'Most major' },
    ],
    services: [
      { title: 'Preventive Care', desc: 'Cleanings, exams, sealants, and fluoride for kids and adults.' },
      { title: 'Cosmetic Dentistry', desc: 'Whitening, veneers, and Invisalign — financing options available.' },
      { title: 'Restorative', desc: 'Same-day crowns, bridges, implants, and root canals in-office.' },
      { title: 'Family-Friendly', desc: 'Kid-comfortable rooms, TVs above every chair, calming sedation options.' },
    ],
    about: {
      heading: 'Two doctors. One philosophy: gentle, modern, no surprises.',
      body: `Dr. Anjali Patel and Dr. Ben Chen opened Riverside in 2007 with a simple goal — build the kind of practice they'd want for their own families. That meant unhurried appointments, transparent pricing, and the latest equipment so you spend less time in the chair.\n\nWe see kids from age one and grown-ups well into their nineties. Same hygienist for years. Same front-desk team that remembers your dog's name. Boise people, taking care of Boise smiles.`,
      signature: 'Dr. Patel & Dr. Chen',
    },
    featured: {
      kicker: 'New patients',
      heading: 'Special offers + popular services',
      navLabel: 'Offers',
      items: [
        { name: 'New Patient Special', desc: 'Exam, X-rays, and basic cleaning for new patients without insurance. Save ~$200.', price: '$99', tag: 'New patients' },
        { name: 'Professional Whitening', desc: 'In-office Zoom whitening — about an hour, average 6–8 shades brighter.', price: '$399' },
        { name: 'Invisalign Consult', desc: 'Free consultation + 3D scan. Full treatment plans from $3,800 with 0% financing.', price: 'Free consult' },
        { name: 'Same-day Crown', desc: 'No second visit, no temporary. CEREC mill on-site, in and out in about 90 minutes.', price: 'from $1,250' },
        { name: 'Kid Checkup', desc: 'Cleaning, fluoride, and a fun chair-side review of brushing technique. TVs help.', price: '$120' },
        { name: 'Emergency Visit', desc: 'Toothache, broken crown, knocked-out tooth. Same-day appointments held daily.', price: 'from $129' },
      ],
    },
    gallery: [
      '/images/demos/riverside-dental/g1.webp',
      '/images/demos/riverside-dental/g2.webp',
      '/images/demos/riverside-dental/g3.webp',
    ],
    hours: [
      { day: 'Mon – Thu', time: '8:00 AM – 5:00 PM' },
      { day: 'Friday', time: '8:00 AM – 1:00 PM' },
      { day: 'Sat – Sun', time: 'Closed' },
    ],
    testimonials: [
      { quote: "I used to dread the dentist. Dr. Patel and the team made my whole family feel relaxed. My 6-year-old now asks when she goes back.", author: 'Jenna L., patient', rating: 5 },
      { quote: 'Invisalign here was a breeze — clear timeline, fair price, the front-desk team caught a way to use my dental + medical insurance together. Saved $1,200.', author: 'Cody R.', rating: 5 },
      { quote: "Cracked a crown the morning of a flight. They got me in within two hours and same-day-milled a new one. I caught my plane.", author: 'Patty O., business traveler', rating: 5 },
    ],
    faq: [
      { q: 'What insurance do you accept?', a: 'Most major plans including Delta Dental, Blue Cross, Cigna, MetLife, Aetna, and United. Call us with your card and we\'ll verify benefits before your visit.' },
      { q: 'Do you offer financing?', a: 'Yes — CareCredit and our in-house plan, both with 0% APR for qualifying treatment up to 24 months. We\'ll talk through it transparently in the consult.' },
      { q: 'Are you taking new patients?', a: 'Yes — kids and adults. Most new-patient appointments book 1–2 weeks out; emergencies get same-day priority.' },
      { q: 'Do you handle emergencies?', a: 'Same-day for existing patients, next-day for new. Call the office and press 1 — front desk routes urgent calls directly.' },
    ],
    contact: { phone: '(208) 555-0117', email: 'smile@riversidedental.example', address: '4055 W State St, Boise, ID' },
  },
  {
    slug: 'greenleaf-landscaping',
    name: 'GreenLeaf Landscape Co.',
    tagline: 'Beautiful yards, year-round. Design, installation, and maintenance for the Front Range.',
    industry: 'Landscaping',
    location: 'Fort Collins, CO',
    cover: '/images/demos/greenleaf-landscaping/cover.webp',
    palettes: {
      light: { bg: '#F4F8F2', surface: '#FFFFFF', text: '#1F2A1A', muted: '#5C6B52', accent: '#15803D', accent2: '#CA8A04' },
      dark:  { bg: '#0E1A10', surface: '#1A2A1B', text: '#ECFCCB', muted: '#A7B89B', accent: '#4ADE80', accent2: '#FACC15' },
    },
    defaultMode: 'light',
    fontDisplay: '"Fraunces", serif',
    stats: [
      { label: 'Years in business', value: '15+' },
      { label: 'Properties served', value: '1,200+' },
      { label: 'Google reviews', value: '4.8 ★' },
      { label: 'Crew members', value: '24' },
    ],
    services: [
      { title: 'Landscape Design', desc: '3D mockups, plant selection, hardscape planning. Free first consultation.' },
      { title: 'Installation', desc: 'Sod, retaining walls, patios, irrigation, lighting — turnkey crews.' },
      { title: 'Seasonal Maintenance', desc: 'Mowing, trimming, fertilization, leaf cleanup, snow removal in winter.' },
      { title: 'Xeriscape & Native', desc: 'Drought-smart designs that thrive in Colorado without weekly watering.' },
    ],
    about: {
      heading: 'Family-run since 2009. Front Range expertise, top to bottom.',
      body: `My dad started GreenLeaf out of a single pickup with a rototiller in the bed. Fifteen years later we run six crews, a design office on Harmony, and a 4-acre nursery just east of town.\n\nWe still operate the same way: walk the property with you, listen to what you actually want, design something that fits your sun, your soil, and your time. Our designers know which natives the deer leave alone. Our crews know which neighborhoods have HOA setbacks. We just know the Front Range.`,
      signature: 'Mark Holloway, Owner',
    },
    featured: {
      kicker: 'Packages',
      heading: 'Most-booked services',
      navLabel: 'Packages',
      items: [
        { name: 'Spring Cleanup', desc: 'Leaf + debris haul, bed edging, mulch refresh, irrigation startup. Average 1/4 acre.', price: 'from $385', tag: 'March – May' },
        { name: 'Design Consultation', desc: '90-minute on-site walkthrough + sketched concept + plant list. Credited toward installation.', price: '$295' },
        { name: 'Weekly Maintenance', desc: 'Mow, trim, edge, blow. Bi-weekly fertilization in season. Cancel anytime.', price: 'from $65 / wk' },
        { name: 'Xeriscape Conversion', desc: 'Replace thirsty turf with drought-smart natives + flagstone. Average 600 sq ft.', price: 'from $4,800' },
        { name: 'Paver Patio', desc: 'Belgard pavers, proper base prep, edge restraints. 12x14 average install.', price: 'from $7,200' },
        { name: 'Holiday Lighting', desc: 'Install + maintain + remove. Commercial-grade LEDs, all power planning included.', price: 'from $480', tag: 'Nov – Jan' },
      ],
    },
    gallery: [
      '/images/demos/greenleaf-landscaping/g1.webp',
      '/images/demos/greenleaf-landscaping/g2.webp',
      '/images/demos/greenleaf-landscaping/g3.webp',
      '/images/demos/greenleaf-landscaping/g4.webp',
    ],
    hours: [
      { day: 'Mon – Fri', time: '7:00 AM – 5:00 PM' },
      { day: 'Saturday', time: '8:00 AM – 12:00 PM' },
      { day: 'Sunday', time: 'Closed' },
    ],
    testimonials: [
      { quote: "GreenLeaf transformed our weed patch into the nicest yard on the block. They were on time, on budget, and the design was better than we imagined.", author: 'Mike & Sara T., homeowners', rating: 5 },
      { quote: 'We converted 1,500 sq ft of dead lawn to xeriscape. Water bill dropped 60% and the yard finally looks like Colorado instead of New Jersey.', author: 'Joe H., Old Town', rating: 5 },
      { quote: 'Their weekly crew is the rare landscaping team that actually shows up when they say they will and does the job the same way every time.', author: 'Lina M., property manager', rating: 5 },
    ],
    faq: [
      { q: 'What\'s your service area?', a: 'Fort Collins, Loveland, Windsor, Timnath, Wellington, and Severance. Design work goes further — we\'ve done jobs in Estes Park and Greeley.' },
      { q: 'How much does a full landscape design cost?', a: 'Design starts at $295 for a single-zone consultation. Full property masters run $1,200–$3,500 depending on size. Design fees credit toward installation.' },
      { q: 'Do you offer maintenance contracts?', a: 'Yes — weekly, bi-weekly, or monthly. Most clients sign annual to lock in pricing. Cancel anytime with two weeks notice; no early-termination fee.' },
      { q: 'Do you specialize in native + drought-tolerant plants?', a: 'Yes — about 40% of our designs are xeriscape or hybrid. We grow 90+ native species in our own nursery, including the deer-resistant cultivars folks ask for most.' },
    ],
    contact: { phone: '(970) 555-0162', email: 'hello@greenleaflandscape.example', address: '2900 Harmony Rd, Fort Collins, CO' },
  },
  {
    slug: 'urban-fitness',
    name: 'Urban Fitness Collective',
    tagline: 'Strength, conditioning, and community. No mirrors, no judgment, just work.',
    industry: 'Gym & Fitness',
    location: 'Austin, TX',
    cover: '/images/demos/urban-fitness/cover.webp',
    palettes: {
      light: { bg: '#FAFAFA', surface: '#FFFFFF', text: '#0A0A0A', muted: '#525252', accent: '#DC2626', accent2: '#EA580C' },
      dark:  { bg: '#0A0A0A', surface: '#171717', text: '#FAFAFA', muted: '#A3A3A3', accent: '#EF4444', accent2: '#F97316' },
    },
    defaultMode: 'dark',
    fontDisplay: '"Bebas Neue", sans-serif',
    stats: [
      { label: 'Active members', value: '600+' },
      { label: 'Google reviews', value: '4.9 ★' },
      { label: 'Coaches on staff', value: '12' },
      { label: 'Classes per week', value: '85' },
    ],
    services: [
      { title: 'Group Strength', desc: 'Coach-led barbell classes. Squat, press, deadlift, repeat. 5 AM to 7 PM.' },
      { title: 'Conditioning', desc: 'Metcons, sled work, sandbags. 30-minute high-output sessions.' },
      { title: '1-on-1 Coaching', desc: 'Personalized programming, form videos, weekly check-ins.' },
      { title: 'Open Gym', desc: 'Members get 24/7 access. Bring your own program, we have the kit.' },
    ],
    about: {
      heading: 'Built by lifters, for lifters.',
      body: `We opened Urban Fitness Collective because we couldn't find a gym that took strength seriously without taking itself seriously. No commercial-gym pop music, no Instagram influencer chasing the rack, no "tone" or "shred" or "bikini" anywhere on the schedule.\n\nJust good barbells, calibrated plates, a sled track that goes 60 feet, and coaches who care more about your fifth set than your first. We coach the basics with intention, we know your name, and we celebrate every PR like it matters — because it does.`,
      signature: 'The Collective',
    },
    featured: {
      kicker: 'Membership',
      heading: 'Pick your level',
      subheading: 'No contracts, no startup fees. Cancel anytime by replying to your renewal email.',
      navLabel: 'Memberships',
      items: [
        { name: '7-Day Trial', desc: 'Unlimited classes + 1 free intro session with a coach. No card required.', price: 'Free', tag: 'New here?' },
        { name: 'Drop-In', desc: 'Single class for visitors or low-frequency lifters.', price: '$25 / class' },
        { name: 'Monthly Unlimited', desc: 'All group classes + 24/7 open gym access. Most popular.', price: '$179 / mo' },
        { name: 'Annual', desc: 'Everything in monthly + 2 free 1-on-1 PT sessions and merch credit.', price: '$1,799 / yr', tag: 'Save $349' },
        { name: '1-on-1 Personal Training', desc: 'Pack of 8 sessions, programmed for your goal, individually scheduled.', price: '$640 / 8 pack' },
        { name: 'Family Add-On', desc: 'Add a partner or kid 14+ to your membership at half price. No extra fees.', price: '+50%' },
      ],
    },
    gallery: [
      '/images/demos/urban-fitness/g1.webp',
      '/images/demos/urban-fitness/g2.webp',
      '/images/demos/urban-fitness/g3.webp',
      '/images/demos/urban-fitness/g4.webp',
    ],
    hours: [
      { day: 'Coach Hours', time: 'Mon – Fri, 5 AM – 8 PM' },
      { day: 'Weekend Classes', time: 'Sat 8 – 11 AM' },
      { day: 'Member Access', time: '24 / 7' },
    ],
    testimonials: [
      { quote: 'Joined for a 30-day trial three years ago. Down 40 pounds, deadlifting 2x my bodyweight, and these are my closest friends now.', author: 'Devin R., member since 2022', rating: 5 },
      { quote: "I'd never touched a barbell. Six months in I'm squatting more than my husband and the coaches make sure my form is perfect every rep.", author: 'Talia P.', rating: 5 },
      { quote: 'Came in skeptical of "community" gyms. Left a year later with a real coach, real progress, and a real second family. Worth every dollar.', author: 'Ben S.', rating: 5 },
    ],
    faq: [
      { q: 'Do I need experience to start?', a: 'No. Every new member starts with a free 1-on-1 intro to teach you the squat, press, and deadlift basics. We meet you where you are.' },
      { q: 'How does the trial work?', a: '7 days, unlimited classes, no card required. Just sign up at the desk or online — we\'ll book your intro session and walk you in for your first class.' },
      { q: 'Can I bring my own program?', a: 'Absolutely. Members get 24/7 open-gym access — bring your spreadsheet, plug in your headphones, work your plan. Coaches are happy to spot.' },
      { q: 'Is there a contract?', a: 'No. Monthly auto-renews until you cancel. Annual is paid up front with a 14-day no-questions refund window. Cancel any time by replying to your renewal email.' },
    ],
    contact: { phone: '(512) 555-0149', email: 'desk@urbanfit.example', address: '1108 E 6th St, Austin, TX' },
  },
];

export default demos;
